/**
 * Intervention Page Component
 *
 * Main page for the intervention management system: manual intervention
 * creation, progress tracking, and analytics. AI-powered recommendations
 * are surfaced as "coming soon" until a predictive model is deployed.
 */

import React from 'react';

import { useParams, useNavigate } from 'react-router-dom';

import { InterventionDashboard } from '../../components';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';

const InterventionPage: React.FC = () => {
  const { studentId } = useParams<{ studentId?: string }>();
  const navigate = useNavigate();
  const isStudentView = Boolean(studentId);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <div>
                <a
                  href="/dashboard"
                  className="text-gray-400 hover:text-gray-500"
                >
                  Dashboard
                </a>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="flex-shrink-0 h-5 w-5 text-gray-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <a
                  href="/interventions"
                  className="ml-4 text-gray-400 hover:text-gray-500"
                >
                  Interventions
                </a>
              </div>
            </li>
            {studentId && (
              <li>
                <div className="flex items-center">
                  <svg
                    className="flex-shrink-0 h-5 w-5 text-gray-300"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="ml-4 text-gray-500" aria-current="page">
                    Student {studentId}
                  </span>
                </div>
              </li>
            )}
          </ol>
        </nav>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isStudentView
                  ? `Student Interventions - ${studentId}`
                  : 'Intervention Management'}
              </h1>
              <p className="mt-2 text-lg text-gray-600">
                {isStudentView
                  ? 'View and manage interventions for this specific student'
                  : 'Create, track, and analyze student interventions'}
              </p>
            </div>
            {isStudentView && (
              <Button
                onClick={() => navigate('/interventions')}
                variant="outline"
                className="ml-4"
              >
                ← Back to Dashboard
              </Button>
            )}
          </div>
        </div>

        {/* Main Content - Intervention Dashboard */}
        <InterventionDashboard studentId={studentId || ''} />

        {/* Help Section */}
        <div className="mt-8">
          <Card className="p-6 bg-blue-50 border-blue-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-blue-900">
                  How to Use the Intervention System
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ol className="list-decimal list-inside space-y-1">
                    <li>
                      <strong>Create Interventions:</strong> Use “New
                      Intervention” to assign an evidence-based intervention to
                      a student with goals and success criteria.
                    </li>
                    <li>
                      <strong>Track Progress:</strong> Add progress notes as
                      the intervention is implemented.
                    </li>
                    <li>
                      <strong>Record Outcomes:</strong> Mark interventions
                      complete with an effectiveness rating, or discontinue
                      them.
                    </li>
                    <li>
                      <strong>Analyze:</strong> Use the Analytics tab to review
                      completion and effectiveness across interventions.
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-gray-500">
          <p>
            Always consult with qualified professionals for student-specific
            interventions.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default InterventionPage;
